import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Button, Container } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook as fabFaFacebook,
  faXTwitter as fabFaXTwitter,
  faDiscord as fabFaDiscord,
  faSteam as fabFaSteam,
  faYoutube as fabFaYoutube,
  faInstagram as fabFaInstagram,
  faRedditAlien as fabFaRedditAlien,
} from '@fortawesome/free-brands-svg-icons';
import { useSelector } from 'react-redux';

import env from 'Env';
import { countryCode } from 'Helpers/Geo/data';
import { supportUrl } from 'Config/lang';
import EmailSubscribe from 'Components/Section/EmailSubscribe/EmailSubscribe';
import LanguageModalButton from 'Components/Modal/LanguageModal/LanguageModalContainer';
import ThemeToggle from 'Components/Toggle/ThemeToggle';
import FooterBannerContainer from 'Components/Banner/FooterBanner/FooterBannerContainer';
import getTrustPilotLogo from 'Helpers/Image/TrustPilotLogo';
import LazyLoad from 'Components/Behaviour/LazyLoading/LazyLoad';
import PaymentIcon from 'Components/Icon/PaymentIcon';


function Footer() {
  const newsletterSubscribed = useSelector(state => state?.auth?.email_newsletter) || false;
  const trustpilot = useSelector(state => state?.trustpilot) || {};
  const pathname = useSelector(state => state?.router?.location?.pathname) || '';
  const siteTheme = useSelector(state => state?.siteTheme) || 'dark';
  const siteBrand = useSelector(state => state?.siteBrand) || 'fanatical';
  const isPayment = pathname && (pathname.includes('/payment') || pathname.includes('/billing'));

  if (isPayment) return false;

  const minimalFooter = pathname && (pathname.includes('/welcome-new-user'));

  const scrollToTop = () => {
    document.body.scrollTop = 0; //  safari
    document.documentElement.scrollTop = 0; // ie, firefox, chrome;
  };

  const trustPilotLogoBlack = getTrustPilotLogo('black');
  const trustPilotLogoWhite = getTrustPilotLogo();

  const paymentMethods = ['paypal', 'visa', 'mastercard', 'googlepay', 'applepay', 'alipay', 'american-express', 'discover', 'jcb', 'ideal', 'sofort', 'unionpay'];

  const isShowcase = location.pathname && location.pathname.includes('/showcase');

  return (
    <footer className={classNames('footer', { 'minimal-footer': minimalFooter }, { 'mt-0': isShowcase })}>
      {!isShowcase &&
        <LazyLoad fallback={<div className="fake-footer-banner" />}>
          <FooterBannerContainer />
        </LazyLoad>
      }

      {!minimalFooter &&
        <div className="desktop-button-container">
          <div className="d-flex w-100">
            <Button
              className="back-to-top-btn w-100"
              onClick={scrollToTop}
            >
              <FormattedMessage id="footer.backtotop" defaultMessage="Back to top" />
            </Button>
          </div>
        </div>
      }

      <div className="footer-ways-to-pay">
        <Container className="links-title-container">
          {window.locale === 'en' &&
            <div className="title-container">
              <FormattedMessage id="footer.payment.pay-title" defaultMessage="Ways to pay:" />
            </div>
          }
          <div className="payment-images-container">
            {paymentMethods.map(paymentMethod =>
              <div className="payment-img-column" key={paymentMethod}>
                <div className="payment-img">
                  <PaymentIcon type={paymentMethod} />
                </div>
              </div>,
            )}
          </div>
        </Container>
      </div>

      <div className={classNames('footer-top', { 'co-brand': siteBrand !== 'fanatical' })}>
        <div id="footer-breadcrumbs" />

        {siteBrand === 'fanatical' && !minimalFooter &&
          <Container className="footer-links-container">
            <div className="footer-links">
              <h2 className="pb-1">
                <span className="d-none d-lg-inline"><FormattedMessage id="footer.browse.prefix" defaultMessage="Browse" />&nbsp;</span>
                <FormattedMessage id="footer.browse.header" defaultMessage="Categories" />
              </h2>
              <Link to="/black-friday">
                <FormattedMessage id="footer.blackfriday" defaultMessage="Black Friday" />
              </Link>
              <Link to="/on-sale">
                <FormattedMessage id="footer.gamedeals" defaultMessage="Game Deals" />
              </Link>
              <Link to="/bundle">
                <FormattedMessage id="footer.bundles" defaultMessage="Bundles" />
              </Link>
              <Link to="/all-games">
                <FormattedMessage id="footer.all-games" defaultMessage="All Games" />
              </Link>
              <Link to="/top-sellers">
                <FormattedMessage id="footer.topsellers" defaultMessage="Top Sellers" />
              </Link>
              <Link to="/latest-deals">
                <FormattedMessage id="footer.latest" defaultMessage="Latest Deals" />
              </Link>
              <Link to="/new-releases">
                <FormattedMessage id="footer.new" defaultMessage="New Releases" />
              </Link>
              <Link to="/upcoming-games">
                <FormattedMessage id="footer.comingsoon" defaultMessage="Upcoming Games" />
              </Link>
              <Link to="/vr-games">
                <FormattedMessage id="footer.vrgames" defaultMessage="VR Games" />
              </Link>
              <Link to="/steam-deck-games">
                <FormattedMessage id="footer.steam-deck-games" defaultMessage="Steam Deck Games" />
              </Link>
              <Link to="/fantasyverse">
                <FormattedMessage id="footer.fantasyverse" defaultMessage="FantasyVerse" />
              </Link>
              <Link to="/franchises">
                <FormattedMessage id="footer.franchises" defaultMessage="Games by Franchise" />
              </Link>
              <Link to="/games-like">
                <FormattedMessage id="footer.games-like" defaultMessage="Games like" />
              </Link>
              {env.giftCardCountries.includes(countryCode) &&
                <Link to="/gift-cards">
                  <FormattedMessage id="footer.gift-cards" defaultMessage="Gift Cards" />
                </Link>
              }
              <Link to="/free-to-play">
                <FormattedMessage id="footer.info.freeToPlay" defaultMessage="Free to Play" />
              </Link>
            </div>

            <div className="footer-links d-none d-sm-flex">
              <h2 className="pb-1"><FormattedMessage id="footer.genre.header" defaultMessage="Games by Genre" /></h2>
              <Link to="/categories/action">
                <FormattedMessage id="footer.genre.action" defaultMessage="Action" />
              </Link>
              <Link to="/categories/adventure">
                <FormattedMessage id="footer.genre.adventure" defaultMessage="Adventure" />
              </Link>
              <Link to="/categories/anime">
                <FormattedMessage id="footer.genre.anime" defaultMessage="Anime" />
              </Link>
              <Link to="/categories/casual">
                <FormattedMessage id="footer.genre.casual" defaultMessage="Casual" />
              </Link>
              <Link to="/categories/co-op">
                <FormattedMessage id="footer.genre.coop" defaultMessage="Co-op" />
              </Link>
              <Link to="/categories/racing">
                <FormattedMessage id="footer.genre.racing" defaultMessage="Racing" />
              </Link>
              <Link to="/categories/rpg">
                <FormattedMessage id="footer.genre.rpg" defaultMessage="RPG" />
              </Link>
              <Link to="/categories/fps">
                <FormattedMessage id="footer.genre.fps" defaultMessage="Shooter" />
              </Link>
              <Link to="/categories/simulation">
                <FormattedMessage id="footer.genre.simulation" defaultMessage="Simulation" />
              </Link>
              <Link to="/categories/sports">
                <FormattedMessage id="footer.genre.sports" defaultMessage="Sports" />
              </Link>
              <Link to="/categories/strategy">
                <FormattedMessage id="footer.genre.strategy" defaultMessage="Strategy" />
              </Link>
              <Link to="/categories">
                <FormattedMessage id="footer.genre.viewall" defaultMessage="View All" />
              </Link>
            </div>

            <div className="footer-links">
              <h2 className="pb-1"><FormattedMessage id="footer.info" defaultMessage="Information" /></h2>
              <Link to="/about-us">
                <FormattedMessage id="footer.info.about" defaultMessage="About Fanatical" />
              </Link>
              <a href={`${supportUrl()}`} target="_blank" rel="noopener noreferrer">
                <FormattedMessage id="footer.info.support" defaultMessage="Support" />
              </a>
              <Link to="/community">
                <FormattedMessage id="footer.info.community" defaultMessage="Community" />
              </Link>
              <Link to="/browser-extension">
                <FormattedMessage id="footer.info.extension" defaultMessage="Fanatical Browser Extension" />
              </Link>
              {window.locale === 'en' &&
                <Link to="/blog">
                  <FormattedMessage id="footer.info.blog" defaultMessage="Fanatical Blog" />
                </Link>
              }
              <Link to="/coupons-vouchers-codes">
                <FormattedMessage id="footer.info.coupons" defaultMessage="Fanatical Coupons" />
              </Link>
              <Link to="/publishers">
                <FormattedMessage id="footer.info.publishers" defaultMessage="Publishing Partners" />
              </Link>
              <Link to="/affiliates">
                <FormattedMessage id="footer.info.affiliates" defaultMessage="Affiliate Partners" />
              </Link>
              <Link to="/redeem-code">
                <FormattedMessage id="footer.info.redeem" defaultMessage="Redeem Code" />
              </Link>
              {countryCode === 'GB' &&
                <a href="https://fanatical.studentbeans.com/" target="_blank" rel="noopener noreferrer nofollow">
                  <FormattedMessage id="footer.info.student" defaultMessage="Student Discount" />
                </a>
              }
              {countryCode === 'GB' &&
                <a href="https://www.youthdiscount.com/yd/fanatical" target="_blank" rel="noopener noreferrer nofollow">
                  16-26 Year Old & Apprentice Discount
                </a>
              }
              <Link to="/gift-guide">
                <FormattedMessage id="footer.info.gaminggiftguide" defaultMessage="Gaming Gift Guide" />
              </Link>
              <Link to="/best-games-of-2025">
                <FormattedMessage id="footer.info.bestgamesof2025" defaultMessage="Best Games of 2025" />
              </Link>
            </div>
            <div className="footer-links email-social-links">
              {!newsletterSubscribed &&
                <div className="email-container">
                  <h2 className="h5 pb-1 email-subscribe-title"><FormattedMessage id="footer.newletter.header" defaultMessage="Join our Newsletter" /></h2>
                  <EmailSubscribe singleOptIn context="Footer" />
                  <p className="small-text">
                    <FormattedMessage
                      id="footer.privacy.message"
                      defaultMessage="You can unsubscribe via the newsletter at any time. By subscribing to our newsletter you agree to our {privacyLink}."
                      values={{
                        privacyLink: <a href={'https://www.fandom.com/privacy-policy'} target="_blank" rel="noopener noreferrer" className="privacy-link">
                          <FormattedMessage id="footer.privacy.link" defaultMessage="Privacy Policy" />
                        </a>,
                      }}
                    />
                  </p>
                </div>
              }
              <div className="social-container">
                <h2 className="pb-1"><FormattedMessage id="footer.connect.header" defaultMessage="Connect with Fanatical" /></h2>

                <div className="footer-social-platforms">
                  <div className="social-platform reddit">
                    <a href="https://www.reddit.com/r/fanatical/" className="footer-social-icon track-link reddit" target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={fabFaRedditAlien} size="lg" title="Reddit, opens in a new tab" />
                    </a>
                  </div>
                  <div className="social-platform facebook">
                    <a href="https://www.facebook.com/wearefanatical" className="footer-social-icon track-link facebook" target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={fabFaFacebook} size="lg" title="Facebook, opens in a new tab" />
                    </a>
                  </div>
                  <div className="social-platform twitter">
                    <a href="https://twitter.com/fanatical" className="footer-social-icon track-link twitter" target="_blank" rel="noopener noreferrer" aria-label="X, opens in a new tab">
                      <FontAwesomeIcon icon={fabFaXTwitter} size="lg" title="X, opens in a new tab" />
                    </a>
                  </div>
                  <div className="social-platform discord">
                    <a href="https://fant.cl/Discord" className="footer-social-icon track-link discord" target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={fabFaDiscord} size="lg" title="Discord, opens in a new tab" />
                    </a>
                  </div>
                  <div className="social-platform steam">
                    <a href="https://steamcommunity.com/groups/wearefanatical" className="footer-social-icon track-link steam" target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={fabFaSteam} size="lg" title="Steam, opens in a new tab" />
                    </a>
                  </div>
                  <div className="social-platform youtube">
                    <a href="https://www.youtube.com/wearefanatical" className="footer-social-icon track-link youtube" target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={fabFaYoutube} size="lg" title="YouTube, opens in a new tab" />
                    </a>
                  </div>
                  <div className="social-platform instagram">
                    <a href="https://www.instagram.com/wearefanatical/" className="footer-social-icon track-link instagram" target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={fabFaInstagram} size="lg" title="Instagram, opens in a new tab" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        }

        {siteBrand === 'fanatical' &&
          <Container>
            <div className="trustpilot-settings-container">
              <div className="trustpilot-container">
                <div className="d-inline-block">
                  <a
                    href="https://www.trustpilot.com/review/www.fanatical.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="trustpilot-footer-link"
                  >
                    <div className="trustpilot-footer-img">
                      <img
                        loading="lazy"
                        src={siteTheme === 'light' ? trustPilotLogoBlack.image : trustPilotLogoWhite.image}
                        alt={trustPilotLogoWhite.alt}
                        height="100%"
                        width="100%"
                      />
                    </div>
                    <div className="trustpilot-text">
                      <FormattedMessage
                        id="footer.trust"
                        defaultMessage='Rated "EXCELLENT" by our customers.'
                      />
                      <span>
                        &nbsp;
                        <FormattedMessage
                          id="footer.trust.reviews"
                          defaultMessage="Based on {count} reviews"
                          values={{
                            count: <FormattedNumber value={trustpilot.total} />,
                          }}
                        />
                      </span>
                    </div>
                  </a>
                </div>
              </div>
              <div className="settings-container">
                <ThemeToggle place="footer" />
                <LanguageModalButton />
              </div>
            </div>
          </Container>
        }
      </div>

      <div className="footer-bottom">
        <Container>
          <div className="footer-bottom-container">
            {siteBrand === 'fanatical' &&
              <div className="fandom-information">
                <div className="fandom-link-container">
                  <a href="https://www.fandom.com/what-is-fandom" target="_blank" rel="noopener noreferrer">
                    <div className="fandom-logo-container">
                      <img
                        loading="lazy"
                        src={siteTheme === 'light' ? 'https://cdn.fanatical.com/production/brand/fandom-logo-dark-horizontal.svg' : 'https://cdn.fanatical.com/production/brand/fandom-logo-light-horizontal.svg'}
                        alt="Fandom"
                      />
                    </div>
                    <div className="fandom-text-container">
                      <FormattedMessage
                        id="footer.fandom-family"
                        defaultMessage="Fanatical is part of the {span} family."
                        values={{
                          span: <span>Fandom</span>,
                        }}
                      />
                    </div>
                  </a>
                </div>
              </div>
            }
            <div className="info-container">
              <div className="legalese paragraph-small">
                © {new Date().getUTCFullYear()} Focus Multimedia Ltd.&nbsp;
                <FormattedMessage id="footer.right" defaultMessage="All rights reserved." />&nbsp;
                <FormattedMessage id="footer.trademarks" defaultMessage="All trademarks are property of their respective owners." />&nbsp;
                <a href={`${supportUrl()}/articles/115005056629-Terms-and-Conditions`}>
                  <FormattedMessage id="footer.terms" defaultMessage="Terms and Conditions" />
                </a>
                &nbsp;/&nbsp;
                <a href={'https://www.fandom.com/privacy-policy'} target="_blank" rel="noopener noreferrer">
                  <FormattedMessage id="footer.privacy" defaultMessage="Privacy Policy" />
                </a>
                &nbsp;/&nbsp;
                <Link to={'/cookie-policy'}>
                  <FormattedMessage id="footer.cookie" defaultMessage="Cookie Policy" />
                </Link>
                &nbsp;/&nbsp;
                <a href={'https://support.fanatical.com/hc/en-us/articles/19478777630738-Accessibility-Statement'} target="_blank" rel="noopener noreferrer">
                  <FormattedMessage id="footer.accessibility-statement" defaultMessage="Accessibility Statement" />
                </a>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
}

export default Footer;
